/**
 * UnifiedTariffSelector - Cascading tariff selector for Vortx Flux Rate Database
 *
 * Provides Utility -> Tariff Schedule -> Option cascading dropdowns.
 */

import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
    Box,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Typography,
    Alert,
    CircularProgress,
    Chip,
} from '@mui/material';
import { ElectricBolt, Schedule } from '@mui/icons-material';
import { useAuth, API_BASE_URL } from '@vortx/shared-ui';

/**
 * @param {Object} props
 * @param {Object} props.selectedTariff - Currently selected tariff data
 * @param {Function} props.onTariffSelect - Callback when tariff selected: (tariffData) => void
 * @param {string} props.matchSource - Source of current match: 'vortx' | null
 * @param {boolean} props.disabled - Disable all controls
 */
function UnifiedTariffSelector({
    selectedTariff,
    onTariffSelect,
    matchSource,
    disabled = false
}) {
    const { currentUser } = useAuth();

    // State
    const [vortxTariffs, setVortxTariffs] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [saveStatus, setSaveStatus] = useState(null); // 'success' | 'duplicate' | 'pending' | null

    // Cascading selection state
    const [selectedUtility, setSelectedUtility] = useState('');
    const [selectedSchedule, setSelectedSchedule] = useState('');
    const [selectedVoltage, setSelectedVoltage] = useState('');
    const [selectedOption, setSelectedOption] = useState('');

    // Fetch Vortx Flux tariffs on mount
    useEffect(() => {
        const fetchVortxTariffs = async () => {
            setLoading(true);
            setError(null);
            try {
                const headers = { 'Content-Type': 'application/json' };
                if (currentUser) {
                    const token = await currentUser.getIdToken();
                    headers['Authorization'] = `Bearer ${token}`;
                }

                const res = await fetch(`${API_BASE_URL}/api/site_dna/v1/projects/custom-rates?tariff_type=electricity`, { headers });
                if (!res.ok) {
                    throw new Error(`Failed to fetch tariffs: ${res.status}`);
                }
                const data = await res.json();
                const rates = Array.isArray(data) ? data : (data.rates || []);
                setVortxTariffs(rates);
            } catch (err) {
                console.error('Error fetching Vortx tariffs:', err);
                setError('Failed to load tariffs');
            } finally {
                setLoading(false);
            }
        };

        fetchVortxTariffs();
    }, [currentUser]);

    // Hydrate dropdowns when selectedTariff changes (e.g., from AI)
    useEffect(() => {
        if (selectedTariff) {
            const utility = selectedTariff.utility_name || selectedTariff.utility || '';
            const schedule = selectedTariff.rate_schedule_name || selectedTariff.rate_schedule || '';
            const voltage = selectedTariff.voltage_level || selectedTariff.rate_data?.voltage_level || '';

            setSelectedUtility(utility);
            setSelectedSchedule(schedule);
            setSelectedVoltage(voltage);
            // Always use tariff id for selectedOption - the dropdown uses id as value
            setSelectedOption(selectedTariff.id || '');
        }
    }, [selectedTariff]);

    // When the tariff is cleared (via CustomRateBuilder "delete"), customTariffData
    // becomes null and the prop falls back to selectedTariffData (same ID, different
    // object). The dropdowns show the old tariff but the user can't re-select it because
    // onChange won't fire for the same value. Detect this reference change and re-notify
    // the parent with the full database entry so customTariffData gets repopulated.
    const prevSelectedRef = useRef(selectedTariff);
    const reNotifiedForId = useRef(null);
    useEffect(() => {
        const prev = prevSelectedRef.current;
        prevSelectedRef.current = selectedTariff;

        // Only act when: reference changed, same tariff ID, and database is loaded
        if (prev === selectedTariff) return;
        if (!prev?.id || !selectedTariff?.id) return;
        if (prev.id !== selectedTariff.id) {
            // Different tariff — reset the re-notification guard
            reNotifiedForId.current = null;
            return;
        }
        if (vortxTariffs.length === 0) return;
        // Only re-notify once per tariff ID to prevent loops
        if (reNotifiedForId.current === selectedTariff.id) return;

        const match = vortxTariffs.find(t => t.id === selectedTariff.id);
        if (match) {
            reNotifiedForId.current = match.id;
            onTariffSelect?.(match);
        }
    }, [selectedTariff, vortxTariffs]); // eslint-disable-line react-hooks/exhaustive-deps

    // Compute cascading options
    const utilities = useMemo(() => {
        const set = new Set(vortxTariffs.map(t => t.utility_name).filter(Boolean));
        return Array.from(set).sort();
    }, [vortxTariffs]);

    const schedulesForUtility = useMemo(() => {
        if (!selectedUtility) return [];
        const filtered = vortxTariffs.filter(t => t.utility_name === selectedUtility);
        const set = new Set(filtered.map(t => t.rate_schedule_name).filter(Boolean));
        return Array.from(set).sort();
    }, [vortxTariffs, selectedUtility]);

    // Get unique voltage levels for the selected schedule
    const voltageLevelsForSchedule = useMemo(() => {
        if (!selectedUtility || !selectedSchedule) return [];
        const filtered = vortxTariffs.filter(t =>
            t.utility_name === selectedUtility &&
            t.rate_schedule_name === selectedSchedule
        );
        const set = new Set(filtered.map(t => t.voltage_level || t.rate_data?.voltage_level).filter(Boolean));
        return Array.from(set).sort();
    }, [vortxTariffs, selectedUtility, selectedSchedule]);

    // Get unique options for the selected voltage level
    const optionsForVoltage = useMemo(() => {
        if (!selectedUtility || !selectedSchedule || !selectedVoltage) return [];
        const filtered = vortxTariffs.filter(t =>
            t.utility_name === selectedUtility &&
            t.rate_schedule_name === selectedSchedule &&
            (t.voltage_level === selectedVoltage || t.rate_data?.voltage_level === selectedVoltage)
        );
        // Extract option from name (e.g., "Option D", "Option E")
        return filtered.map(t => {
            const name = t.name || '';
            const optionMatch = name.match(/Option\s*([A-Z])/i);
            const optionLabel = optionMatch ? `Option ${optionMatch[1].toUpperCase()}` : name;
            return { ...t, optionLabel };
        });
    }, [vortxTariffs, selectedUtility, selectedSchedule, selectedVoltage]);

    // Handle utility change
    const handleUtilityChange = (e) => {
        const utility = e.target.value;
        setSelectedUtility(utility);
        setSelectedSchedule('');
        setSelectedVoltage('');
        setSelectedOption('');
        setSaveStatus(null);
    };

    // Handle schedule change
    const handleScheduleChange = (e) => {
        const schedule = e.target.value;
        setSelectedSchedule(schedule);
        setSelectedVoltage('');
        setSelectedOption('');
        setSaveStatus(null);

        // Auto-select if only one tariff for this schedule
        const options = vortxTariffs.filter(t =>
            t.utility_name === selectedUtility &&
            t.rate_schedule_name === schedule
        );
        if (options.length === 1) {
            const tariff = options[0];
            setSelectedVoltage(tariff.voltage_level || tariff.rate_data?.voltage_level || '');
            setSelectedOption(tariff.id);
            onTariffSelect?.(tariff);
        }
    };

    // Handle voltage change
    const handleVoltageChange = (e) => {
        const voltage = e.target.value;
        setSelectedVoltage(voltage);
        setSelectedOption('');
        setSaveStatus(null);

        // Auto-select if only one option for this voltage
        const options = vortxTariffs.filter(t =>
            t.utility_name === selectedUtility &&
            t.rate_schedule_name === selectedSchedule &&
            (t.voltage_level === voltage || t.rate_data?.voltage_level === voltage)
        );
        if (options.length === 1) {
            setSelectedOption(options[0].id);
            onTariffSelect?.(options[0]);
        }
    };

    // Handle option/tariff change
    const handleOptionChange = (e) => {
        const tariffId = e.target.value;
        setSelectedOption(tariffId);
        setSaveStatus(null);

        const tariff = vortxTariffs.find(t => t.id === tariffId);
        if (tariff) {
            onTariffSelect?.(tariff);
        }
    };

    // Determine badge for current tariff
    const renderSourceBadge = () => {
        if (!selectedTariff) return null;

        const source = matchSource || selectedTariff.match_source;

        if (source === 'vortx') {
            return (
                <Chip
                    icon={<ElectricBolt />}
                    label="Vortx Flux"
                    size="small"
                    color="primary"
                    sx={{ ml: 1 }}
                />
            );
        } else if (saveStatus === 'pending') {
            return (
                <Chip
                    icon={<Schedule />}
                    label="Pending Approval"
                    size="small"
                    color="warning"
                    sx={{ ml: 1 }}
                />
            );
        }
        return null;
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 2 }}>
                <CircularProgress size={20} />
                <Typography variant="body2">Loading tariffs...</Typography>
            </Box>
        );
    }

    return (
        <Box>
            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
            )}

            {/* Cascading Dropdowns */}
            <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                {/* Utility Dropdown */}
                <FormControl size="small" sx={{ minWidth: 180 }}>
                    <InputLabel>Utility</InputLabel>
                    <Select
                        label="Utility"
                        value={selectedUtility}
                        onChange={handleUtilityChange}
                        disabled={disabled || vortxTariffs.length === 0}
                    >
                        {utilities.map(u => (
                            <MenuItem key={u} value={u}>
                                <ElectricBolt sx={{ fontSize: 16, mr: 1, color: 'primary.main' }} />
                                {u}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                {/* Tariff Schedule Dropdown */}
                {selectedUtility && (schedulesForUtility.length > 0 || selectedSchedule) && (
                    <FormControl size="small" sx={{ minWidth: 200 }}>
                        <InputLabel>Tariff Schedule</InputLabel>
                        <Select
                            label="Tariff Schedule"
                            value={selectedSchedule}
                            onChange={handleScheduleChange}
                            disabled={disabled}
                        >
                            {schedulesForUtility.map(s => (
                                <MenuItem key={s} value={s}>{s}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                )}

                {/* Voltage Level Dropdown */}
                {selectedSchedule && (voltageLevelsForSchedule.length > 0 || selectedVoltage) && (
                    <FormControl size="small" sx={{ minWidth: 160 }}>
                        <InputLabel>Voltage Level</InputLabel>
                        <Select
                            label="Voltage Level"
                            value={selectedVoltage}
                            onChange={handleVoltageChange}
                            disabled={disabled}
                        >
                            {voltageLevelsForSchedule.map(v => (
                                <MenuItem key={v} value={v}>{v}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                )}

                {/* Option Dropdown (only show if multiple options for this voltage, or if option is already selected) */}
                {selectedVoltage && (optionsForVoltage.length > 1 || selectedOption) && (
                    <FormControl size="small" sx={{ minWidth: 140 }}>
                        <InputLabel>Option</InputLabel>
                        <Select
                            label="Option"
                            value={selectedOption}
                            onChange={handleOptionChange}
                            disabled={disabled}
                        >
                            {optionsForVoltage.map(o => (
                                <MenuItem key={o.id} value={o.id}>
                                    {o.optionLabel}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                )}
            </Box>

            {/* Selected Tariff Display */}
            {selectedTariff && (
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                        Selected: <strong>{selectedTariff.name || `${selectedUtility} ${selectedSchedule}`}</strong>
                    </Typography>
                    {renderSourceBadge()}
                </Box>
            )}

            {/* Save Status Messages */}
            {saveStatus === 'success' && (
                <Alert severity="success" sx={{ mb: 2 }}>
                    Tariff submitted for admin approval. You can continue using it now.
                </Alert>
            )}
            {saveStatus === 'duplicate' && (
                <Alert severity="info" sx={{ mb: 2 }}>
                    This tariff already exists in the Vortx Flux database.
                </Alert>
            )}
            {saveStatus === 'pending' && (
                <Alert severity="info" sx={{ mb: 2 }}>
                    This tariff is already pending admin approval.
                </Alert>
            )}
        </Box>
    );
}

export default UnifiedTariffSelector;
